// File-backed sound definitions (ogg/mp3 assets under /assets/audio).
// Split out of sounds.ts so each def table lives beside its kind.

import type { SoundDef } from './SoundManager';

const AUDIO = '/assets/audio';

export const fileDefs: Record<string, SoundDef> = {
  // Combat
  hit: {
    type: 'file',
    url: `${AUDIO}/combat/sword-hits-armor.mp3`,
    variants: [
      `${AUDIO}/combat/sword-hits-armor.mp3`,
      `${AUDIO}/combat/sword-hits-shield.mp3`,
      `${AUDIO}/combat/sword-hits-sword.mp3`,
      `${AUDIO}/combat/punch-hit.mp3`,
    ],
    gain: 0.45,
  },
  arrow_hit: {
    type: 'file',
    url: `${AUDIO}/combat/arrow-impact-body-1.mp3`,
    variants: [
      `${AUDIO}/combat/arrow-impact-body-1.mp3`,
      `${AUDIO}/combat/arrow-impact-body-2.mp3`,
    ],
    gain: 0.4,
  },
  shoot_arrow: {
    type: 'file',
    url: `${AUDIO}/combat/shoot-arrow-1.mp3`,
    variants: [
      `${AUDIO}/combat/shoot-arrow-1.mp3`,
      `${AUDIO}/combat/shoot-arrow-2.mp3`,
      `${AUDIO}/combat/shoot-arrow-3.mp3`,
    ],
    gain: 0.35,
  },
  shield_block: {
    type: 'file',
    url: `${AUDIO}/combat/shield-block.mp3`,
    gain: 0.4,
  },
  death: {
    type: 'file',
    url: `${AUDIO}/combat/death-1.mp3`,
    variants: [
      `${AUDIO}/combat/death-1.mp3`,
      `${AUDIO}/combat/death-2.mp3`,
      `${AUDIO}/combat/death-3.mp3`,
    ],
    gain: 0.5,
  },

  // Unit
  move: {
    type: 'file',
    url: `${AUDIO}/unit/move-step.mp3`,
    variants: [
      `${AUDIO}/unit/move-step.mp3`,
      `${AUDIO}/unit/move-step-2.mp3`,
      `${AUDIO}/unit/move-step-3.mp3`,
    ],
    gain: 0.25,
  },
  garrison: {
    type: 'file',
    url: `${AUDIO}/city/garrison.mp3`,
    gain: 0.35,
  },
  deploy: {
    type: 'file',
    url: `${AUDIO}/unit/deploy.mp3`,
    gain: 0.35,
  },
  level_up: {
    type: 'file',
    url: `${AUDIO}/unit/level-up.mp3`,
    gain: 0.45,
  },
  upgrade: {
    type: 'file',
    url: `${AUDIO}/unit/upgrade.mp3`,
    gain: 0.4,
  },

  // City
  city_damaged: {
    type: 'file',
    url: `${AUDIO}/city/damaged.mp3`,
    gain: 0.4,
  },
  captured: {
    type: 'file',
    url: `${AUDIO}/city/captured.mp3`,
    gain: 0.5,
  },
  build_done: {
    type: 'file',
    url: `${AUDIO}/city/build-complete.mp3`,
    gain: 0.45,
  },
  training_complete: {
    type: 'file',
    url: `${AUDIO}/city/training-complete.mp3`,
    gain: 0.4,
  },

  // UI
  button_click: {
    type: 'file',
    url: `${AUDIO}/ui/button-click.mp3`,
    gain: 0.25,
  },
  button_hover: {
    type: 'file',
    url: `${AUDIO}/ui/button-hover.mp3`,
    gain: 0.15,
  },
  panel_open: {
    type: 'file',
    url: `${AUDIO}/ui/panel-open.mp3`,
    gain: 0.2,
  },
  panel_close: {
    type: 'file',
    url: `${AUDIO}/ui/panel-close.mp3`,
    gain: 0.2,
  },
  notification: {
    type: 'file',
    url: `${AUDIO}/ui/notification.mp3`,
    gain: 0.3,
  },
  error: {
    type: 'file',
    url: `${AUDIO}/ui/error.mp3`,
    gain: 0.3,
  },
  doctrine_spend: {
    type: 'file',
    url: `${AUDIO}/ui/doctrine-spend.mp3`,
    gain: 0.35,
  },
  match_start: {
    type: 'file',
    url: `${AUDIO}/ui/match-start.mp3`,
    gain: 0.5,
  },
  achievement: {
    type: 'file',
    url: `${AUDIO}/ui/achievement-1.mp3`,
    variants: [`${AUDIO}/ui/achievement-1.mp3`, `${AUDIO}/ui/achievement-3.mp3`],
    gain: 0.45,
  },
  discover_magic: {
    type: 'file',
    url: `${AUDIO}/ui/discover-magic-item.mp3`,
    gain: 0.4,
  },
  discover_treasure: {
    type: 'file',
    url: `${AUDIO}/ui/discover-treasure.mp3`,
    gain: 0.4,
  },
  fanfare: {
    type: 'file',
    url: `${AUDIO}/ui/arena_horn-1.mp3`,
    variants: [
      `${AUDIO}/ui/arena_horn-1.mp3`,
      `${AUDIO}/ui/arena_horn-2.mp3`,
      `${AUDIO}/ui/arena_horn-3.mp3`,
      `${AUDIO}/ui/arena_horn-4.mp3`,
    ],
    gain: 0.55,
  },
  trumpets: {
    type: 'file',
    url: `${AUDIO}/ui/trumpets-1.mp3`,
    gain: 0.5,
  },

  // Music
  music_title: {
    type: 'file',
    url: `${AUDIO}/music/title-theme.mp3`,
    gain: 0.5,
  },
  music_peace: {
    type: 'file',
    url: `${AUDIO}/music/peace-theme.mp3`,
    gain: 0.4,
  },
  music_combat: {
    type: 'file',
    url: `${AUDIO}/music/combat-theme.mp3`,
    gain: 0.5,
  },
  music_victory: {
    type: 'file',
    url: `${AUDIO}/music/victory-theme.mp3`,
    gain: 0.55,
  },
  music_defeat: {
    type: 'file',
    url: `${AUDIO}/music/defeat-theme.mp3`,
    gain: 0.5,
  },
  music_doctrine_arcane: {
    type: 'file',
    url: `${AUDIO}/music/doctrine-arcane.mp3`,
    gain: 0.4,
  },
  music_doctrine_chaos: {
    type: 'file',
    url: `${AUDIO}/music/doctrine-chaos.mp3`,
    gain: 0.45,
  },
  music_doctrine_faith: {
    type: 'file',
    url: `${AUDIO}/music/doctrine-faith.mp3`,
    gain: 0.4,
  },
  music_doctrine_industry: {
    type: 'file',
    url: `${AUDIO}/music/doctrine-industry.mp3`,
    gain: 0.4,
  },
  music_doctrine_nature: {
    type: 'file',
    url: `${AUDIO}/music/doctrine-nature.mp3`,
    gain: 0.4,
  },

  // Ambient
  ambient_plains: {
    type: 'file',
    url: `${AUDIO}/ambient/plains-wind.mp3`,
    gain: 0.25,
  },
  ambient_forest: {
    type: 'file',
    url: `${AUDIO}/ambient/forest-birds.mp3`,
    gain: 0.3,
  },
  ambient_desert: {
    type: 'file',
    url: `${AUDIO}/ambient/desert-wind.mp3`,
    gain: 0.25,
  },
  ambient_mountain: {
    type: 'file',
    url: `${AUDIO}/ambient/mountain-wind.mp3`,
    gain: 0.2,
  },
  ambient_tundra: {
    type: 'file',
    url: `${AUDIO}/ambient/tundra-wind.mp3`,
    gain: 0.2,
  },
  ambient_coast: {
    type: 'file',
    url: `${AUDIO}/ambient/coast-waves.mp3`,
    gain: 0.3,
  },
  ambient_city: {
    type: 'file',
    url: `${AUDIO}/ambient/city-ambient.mp3`,
    gain: 0.25,
  },

  // Spells (FR-6.2)
  spell_healing_light: {
    type: 'file',
    url: `${AUDIO}/combat/spell-heal.mp3`,
    gain: 0.4,
  },
  spell_teleport: {
    type: 'file',
    url: `${AUDIO}/combat/spell-teleport.mp3`,
    gain: 0.5,
  },
  spell_divine_shield: {
    type: 'file',
    url: `${AUDIO}/combat/spell-shield.mp3`,
    gain: 0.45,
  },
  spell_summon_skeleton: {
    type: 'file',
    url: `${AUDIO}/combat/spell-summon.mp3`,
    gain: 0.5,
  },
  spell_lich_drain: {
    type: 'file',
    url: `${AUDIO}/combat/spell-drain.mp3`,
    gain: 0.5,
  },

  // Faith (FR-6.3)
  faith_sacrifice: {
    type: 'file',
    url: `${AUDIO}/combat/faith-sacrifice.mp3`,
    gain: 0.5,
  },
  faith_crisis: {
    type: 'file',
    url: `${AUDIO}/ui/faith-crisis.mp3`,
    gain: 0.55,
  },
  sanctified_pulse: {
    type: 'file',
    url: `${AUDIO}/ui/sanctified-pulse.mp3`,
    gain: 0.3,
  },

  // Espionage (FR-6.4)
  assassin_success: {
    type: 'file',
    url: `${AUDIO}/combat/assassin-success.mp3`,
    gain: 0.5,
  },
  assassin_failure: {
    type: 'file',
    url: `${AUDIO}/ui/assassin-failure.mp3`,
    gain: 0.35,
  },
  sabotage_planted: {
    type: 'file',
    url: `${AUDIO}/combat/sabotage-plant.mp3`,
    gain: 0.4,
  },
  propaganda: {
    type: 'file',
    url: `${AUDIO}/ui/propaganda.mp3`,
    gain: 0.4,
  },
  intel_gathered: {
    type: 'file',
    url: `${AUDIO}/ui/intel-gathered.mp3`,
    gain: 0.3,
  },

  // Trade (FR-6.7)
  convoy_departed: {
    type: 'file',
    url: `${AUDIO}/unit/convoy-depart.mp3`,
    gain: 0.35,
  },
  convoy_arrived: {
    type: 'file',
    url: `${AUDIO}/unit/convoy-arrive.mp3`,
    gain: 0.4,
  },
  convoy_intercepted: {
    type: 'file',
    url: `${AUDIO}/combat/convoy-intercept.mp3`,
    gain: 0.5,
  },
};
